"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Select, SelectOption } from "@/components/ui/select";
import DatePicker from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar, Filter, X } from "lucide-react";
import { format } from "date-fns";

interface DateFilterProps {
  className?: string;
}

export default function DateFilter({ className }: DateFilterProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  // Get the current period from URL params
  const currentPeriod = searchParams.get("period") || "all";
  const startDateStr = searchParams.get("startDate");
  const endDateStr = searchParams.get("endDate");

  // Parse dates from URL
  const [startDate, setStartDate] = useState<Date | null>(
    startDateStr ? new Date(startDateStr) : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    endDateStr ? new Date(endDateStr) : null
  );

  // Track if we're showing the custom date range
  const [showCustomRange, setShowCustomRange] = useState(
    currentPeriod === "custom"
  );

  // Period options
  const periodOptions: SelectOption[] = [
    { value: "all", label: "All Time" },
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "3months", label: "Last 3 Months" },
    { value: "quarter", label: "This Quarter" },
    { value: "6months", label: "Last 6 Months" },
    { value: "year", label: "This Year" },
    { value: "custom", label: "Custom Range" },
  ];

  // When URL params change, update local state
  useEffect(() => {
    setShowCustomRange(currentPeriod === "custom");

    if (startDateStr) {
      const parsedDate = new Date(startDateStr);
      setStartDate(parsedDate || null);
    } else {
      setStartDate(null);
    }

    if (endDateStr) {
      const parsedDate = new Date(endDateStr);
      setEndDate(parsedDate || null);
    } else {
      setEndDate(null);
    }
  }, [currentPeriod, startDateStr, endDateStr]);

  // Create a new search params object and redirect
  const updateUrlWithFilters = (
    period: string,
    start?: Date | null,
    end?: Date | null
  ) => {
    const params = new URLSearchParams(searchParams.toString());

    // Always set the period
    params.set("period", period);

    // Only include dates if they're valid and the period is custom
    if (period === "custom") {
      if (start) params.set("startDate", start.toISOString().split("T")[0]);
      else params.delete("startDate");

      if (end) params.set("endDate", end.toISOString().split("T")[0]);
      else params.delete("endDate");
    } else {
      // For non-custom periods, remove date params
      params.delete("startDate");
      params.delete("endDate");
    }

    // Update URL with the new params
    router.push(`?${params.toString()}`);
  };

  // Handle period change
  const handlePeriodChange = (option: SelectOption | SelectOption[] | null) => {
    if (!option || Array.isArray(option)) return;

    // If custom is selected, show the custom range inputs
    setShowCustomRange(option.value === "custom");

    // Update URL only if not switching to custom (for custom we'll wait for Apply button)
    if (option.value !== "custom") {
      updateUrlWithFilters(option.value);
    }
  };

  // Handle apply custom range
  const handleApplyCustomRange = () => {
    if (startDate && endDate) {
      // Only update if both dates are selected
      updateUrlWithFilters("custom", startDate, endDate);
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    router.push("?"); // Clear all params
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className='flex flex-col md:flex-row gap-4'>
        <div className='flex-1'>
          <Select
            options={periodOptions}
            selected={
              periodOptions.find((option) => option.value === currentPeriod) ||
              periodOptions[0]
            }
            onChange={handlePeriodChange}
            placeholder='Select time period'
            label='Time Period'
          />
        </div>

        {showCustomRange && (
          <>
            <div className='w-full md:w-auto'>
              <div className='space-y-2'>
                <Label>Start Date</Label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                />
              </div>
            </div>

            <div className='w-full md:w-auto'>
              <div className='space-y-2'>
                <Label>End Date</Label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                />
              </div>
            </div>

            <div className='flex items-end gap-2'>
              <Button
                onClick={handleApplyCustomRange}
                disabled={!startDate || !endDate}
              >
                <Filter className='mr-2 h-4 w-4' />
                Apply
              </Button>
            </div>
          </>
        )}

        {(currentPeriod !== "all" || startDate || endDate) && (
          <div className='flex items-end'>
            <Button variant='ghost' size='sm' onClick={handleClearFilters}>
              <X className='mr-2 h-4 w-4' />
              Clear Filters
            </Button>
          </div>
        )}
      </div>

      {showCustomRange && startDate && endDate && (
        <div className='text-sm text-muted-foreground flex items-center gap-2'>
          <Calendar className='h-4 w-4' />
          <span>
            Showing data from <strong>{format(startDate, "PPP")}</strong> to{" "}
            <strong>{format(endDate, "PPP")}</strong>
          </span>
        </div>
      )}
    </div>
  );
}
